import { Box } from '@mui/material';
import { ReactNode } from 'react';

import { ExpandableText } from './ExpandableText';

const CELL_MAX_WIDTH = 240;

type EditableTextCellProps = {
    text: string | null;
    title: string;
    subtitle?: ReactNode;
    /** When false, renders `noViewMessage` instead of the text. Default true. */
    canView?: boolean;
    /** When false, the popover stays read-only even if `onSave` is provided. Default true. */
    canEdit?: boolean;
    /** Message shown when `canView` is false. */
    noViewMessage?: string;
    /** Optional `data-testid` for the no-view fallback span. */
    noViewTestId?: string;
    /** When provided AND `canEdit` is true, enables in-popover editing with Save/Cancel. */
    onSave?: (text: string) => Promise<void>;
};

/**
 * Thin wrapper around `ExpandableText` that adds the canView/canEdit gating pattern.
 * Use this as the shared base for any table cell that shows truncated text with a
 * click-to-expand popover and optional inline editing (e.g. patient notes, snooze reason).
 */
export const EditableTextCell = ({
    text,
    title,
    subtitle,
    canView = true,
    canEdit = true,
    noViewMessage = 'No viewing permissions',
    noViewTestId,
    onSave,
}: EditableTextCellProps) => {
    if (!canView) {
        return <span data-testid={noViewTestId}>{noViewMessage}</span>;
    }
    return (
        <Box sx={{ maxWidth: CELL_MAX_WIDTH }}>
            <ExpandableText
                text={text}
                title={title}
                subtitle={subtitle}
                onSave={canEdit ? onSave : undefined}
            />
        </Box>
    );
};
